import { posthogApiHost } from "./firstParty"

const POSTHOG_API_KEY = process.env.GATSBY_POSTHOG_API_KEY
const USE_POSTHOG = process.env.GATSBY_USE_POSTHOG === "true"

let initialized = false
let posthogClient = null
let posthogImport = null
let initializationPromise = null

function isDevHost() {
  if (typeof window === "undefined") return false
  const hostname = window.location.hostname
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname.startsWith("dev.")
  )
}

function hasStatisticsConsent() {
  if (typeof window === "undefined") return false
  if (isDevHost()) return true
  return (
    window.Cookiebot &&
    window.Cookiebot.consent &&
    window.Cookiebot.consent.statistics
  )
}

async function loadPosthog() {
  if (!posthogImport) {
    posthogImport = import("posthog-js")
      .then(module => module.default)
      .catch(error => {
        // A transient chunk/network failure must not make analytics impossible
        // for the remainder of the session.
        posthogImport = null
        posthogClient = null
        throw error
      })
  }
  posthogClient = await posthogImport
  return posthogClient
}

async function doInit() {
  if (!hasStatisticsConsent()) return
  if (initialized) {
    posthogClient?.opt_in_capturing?.()
    return posthogClient
  }
  if (initializationPromise) return initializationPromise

  initializationPromise = (async () => {
    const posthog = await loadPosthog()
    // Consent may have been withdrawn while the optional analytics chunk loaded.
    if (!hasStatisticsConsent()) return null

    posthog.init(POSTHOG_API_KEY, {
      api_host: posthogApiHost(),
      ui_host: "https://eu.posthog.com",
      persistence: "localStorage+cookie",
      cross_subdomain_cookie: true,
      mask_all_text: true,
      capture_pageview: true,
      capture_pageleave: true,
      custom_campaign_params: [
        "gad_medium",
        "gad_campaignid",
        "gad_term",
        "gad_content",
        "klang_trackingid",
      ],
    })

    // A previous Cookiebot decline may have persisted PostHog's opt-out flag.
    // Current statistics consent is authoritative, so clear that flag before
    // treating the client as initialized.
    posthog.opt_in_capturing?.()
    initialized = true
    return posthog
  })().finally(() => {
    initializationPromise = null
  })

  return initializationPromise
}

export function initPosthog() {
  if (typeof window === "undefined") return null
  if (!USE_POSTHOG || !POSTHOG_API_KEY) return null

  // Try to initialize immediately if consent already given
  void doInit().catch(() => {})

  // Listen for consent changes
  window.addEventListener("CookiebotOnAccept", () => {
    void doInit().catch(() => {})
  })
  window.addEventListener("CookiebotOnDecline", () => {
    if (initialized && posthogClient) {
      posthogClient.opt_out_capturing()
    }
  })

  return initialized ? posthogClient : null
}

// Capture a custom event. Like pageviews, it is only sent with statistics
// consent; without it the call is a no-op.
export function capturePosthogEvent(event, properties) {
  if (typeof window === "undefined") return
  if (!USE_POSTHOG || !POSTHOG_API_KEY || !hasStatisticsConsent()) return
  void doInit()
    .then(posthog => posthog?.capture?.(event, properties))
    .catch(() => {})
}

export function getPosthogDistinctId() {
  if (!hasStatisticsConsent()) return null
  if (!posthogClient?.__loaded) return null
  if (posthogClient.has_opted_out_capturing?.()) return null
  return posthogClient.get_distinct_id?.() || null
}
